import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { wholesalerAPI } from '../../services/api';
import { format, subDays, eachDayOfInterval, eachMonthOfInterval, subMonths } from 'date-fns';
import { BarChart3, Users, Package, ShoppingCart, TrendingUp, ArrowUpRight, ArrowDownRight, Minus, Euro, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// ================================================================
// SVG CHART COMPONENTS
// ================================================================

const BarChart = ({ data, color = '#6366f1', height = 200, valuePrefix = '' }) => {
  if (!data || data.length === 0) return <div className="text-center text-gray-400 py-10 text-sm">No data available</div>;
  const values = data.map(d => d.value || 0);
  const maxVal = Math.max(...values, 1);
  const pad = { t: 25, r: 10, b: 35, l: valuePrefix === '€' ? 50 : 35 };
  const W = 500, H = height;
  const iW = W - pad.l - pad.r, iH = H - pad.t - pad.b;
  const barW = Math.min((iW / data.length) * 0.55, 40);
  const gap = iW / data.length;
  const yTicks = Array.from({ length: 5 }, (_, i) => Math.round((maxVal / 4) * i));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: `${H}px` }}>
      <defs>
        <linearGradient id={`bg-${color.slice(1)}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} /><stop offset="100%" stopColor={color} stopOpacity="0.35" />
        </linearGradient>
      </defs>
      {yTicks.map((v, i) => {
        const y = pad.t + iH - (v / maxVal) * iH;
        return (<g key={i}><line x1={pad.l} y1={y} x2={W - pad.r} y2={y} stroke="#f0f0f0" strokeWidth="1" />
          <text x={pad.l - 6} y={y + 4} textAnchor="end" fontSize="10" fill="#9ca3af">{valuePrefix}{v >= 1000 ? `${(v/1000).toFixed(1)}k` : v}</text></g>);
      })}
      {data.map((d, i) => {
        const v = d.value || 0;
        const bH = (v / maxVal) * iH;
        const x = pad.l + i * gap + (gap - barW) / 2;
        const y = pad.t + iH - bH;
        return (<g key={i}>
          <rect x={x} y={y} width={barW} height={Math.max(bH, 1)} rx="3" fill={`url(#bg-${color.slice(1)})`} />
          {v > 0 && <text x={x + barW / 2} y={y - 5} textAnchor="middle" fontSize="10" fontWeight="600" fill="#374151">{valuePrefix}{v >= 1000 ? `${(v/1000).toFixed(1)}k` : v.toFixed(valuePrefix === '€' ? 0 : 0)}</text>}
          <text x={x + barW / 2} y={H - pad.b + 16} textAnchor="middle" fontSize="9.5" fill="#6b7280">{d.label}</text>
        </g>);
      })}
    </svg>
  );
};

const DonutChart = ({ data, size = 150 }) => {
  const statusColors = { pending: '#f59e0b', confirmed: '#6366f1', processing: '#3b82f6', shipped: '#8b5cf6', delivered: '#10b981', cancelled: '#ef4444', in_progress: '#3b82f6' };
  const entries = Object.entries(data || {}).filter(([_, v]) => v > 0);
  const total = entries.reduce((s, [_, v]) => s + v, 0);
  if (total === 0) return <div className="text-center text-gray-400 py-6 text-sm">No orders</div>;
  const R = 55, CX = 75, CY = 75, C = 2 * Math.PI * R;
  let offset = 0;
  return (
    <div className="flex items-center justify-center gap-6 py-2">
      <svg width={size} height={size} viewBox="0 0 150 150">
        {entries.map(([status, count]) => {
          const dash = (count / total) * C; const o = offset; offset += dash;
          return <circle key={status} cx={CX} cy={CY} r={R} fill="none" stroke={statusColors[status] || '#94a3b8'}
            strokeWidth="18" strokeDasharray={`${dash} ${C - dash}`} strokeDashoffset={-o} transform={`rotate(-90 ${CX} ${CY})`} />;
        })}
        <text x={CX} y={CY - 4} textAnchor="middle" fontSize="24" fontWeight="700" fill="#111827">{total}</text>
        <text x={CX} y={CY + 12} textAnchor="middle" fontSize="10" fill="#6b7280">orders</text>
      </svg>
      <div className="flex flex-col gap-1.5">
        {entries.map(([status, count]) => (
          <div key={status} className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: statusColors[status] || '#94a3b8' }} />
            <span className="text-xs text-gray-600 capitalize w-16">{status.replace('_', ' ')}</span>
            <span className="text-xs font-bold text-gray-900">{count}</span>
            <span className="text-xs text-gray-400">({Math.round(count / total * 100)}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ================================================================
// MAIN PAGE
// ================================================================
const WholesalerAnalytics = () => {
  const { t } = useTranslation('wholesaler');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [timeRange, setTimeRange] = useState('30days');
  const [selectedCustomer, setSelectedCustomer] = useState('all');
  const [chartData, setChartData] = useState({ revenue: [], orders: [] });
  const [tier, setTier] = useState('starter');
  const [expandedCustomers, setExpandedCustomers] = useState(false);
  const [expandedProducts, setExpandedProducts] = useState(false);
  const [expandedCustomerDetail, setExpandedCustomerDetail] = useState(null);

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { if (orders.length > 0) processChartData(orders); }, [timeRange, selectedCustomer, orders]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ordersRes, productsRes, profileRes] = await Promise.all([
        wholesalerAPI.getOrders(),
        wholesalerAPI.getProducts(),
        wholesalerAPI.getProfile()
      ]);
      const ordersList = ordersRes.data.data || ordersRes.data.orders || ordersRes.data || [];
      const productsList = productsRes.data.data || productsRes.data.products || productsRes.data || [];
      const wholesaler = profileRes.data.wholesaler || profileRes.data;
      setTier(wholesaler?.subscriptionTier || 'starter');
      const safeOrders = Array.isArray(ordersList) ? ordersList : [];
      setOrders(safeOrders);
      setProducts(Array.isArray(productsList) ? productsList : []);
      const uniqueBusinesses = [...new Map(safeOrders.filter(o => o.businessId).map(o => [o.businessId._id, o.businessId])).values()];
      setBusinesses(uniqueBusinesses);
      processChartData(safeOrders);
    } catch (err) { console.error('Analytics error:', err); }
    finally { setLoading(false); }
  };

  const processChartData = (ordersList) => {
    if (!Array.isArray(ordersList) || ordersList.length === 0) { setChartData({ revenue: [], orders: [] }); return; }
    const now = new Date();
    let startDate;
    switch (timeRange) {
      case '7days': startDate = subDays(now, 7); break;
      case '90days': startDate = subDays(now, 90); break;
      case 'year': startDate = subMonths(now, 11); break;
      default: startDate = subDays(now, 30);
    }
    let filtered = ordersList.filter(o => new Date(o.createdAt) >= startDate && ['delivered', 'shipped'].includes(o.status));
    if (selectedCustomer !== 'all') filtered = filtered.filter(o => o.businessId?._id === selectedCustomer);

    if (timeRange === 'year') {
      const months = eachMonthOfInterval({ start: startDate, end: now });
      const data = months.map(m => {
        const mo = filtered.filter(o => { const d = new Date(o.createdAt); return d.getMonth() === m.getMonth() && d.getFullYear() === m.getFullYear(); });
        return { label: format(m, 'MMM'), revenue: mo.reduce((s, o) => s + (o.totals?.total || 0), 0), count: mo.length };
      });
      setChartData({ revenue: data.map(d => ({ label: d.label, value: d.revenue })), orders: data.map(d => ({ label: d.label, value: d.count })) });
    } else {
      const days = eachDayOfInterval({ start: startDate, end: now });
      const data = days.map(day => {
        const dayOrders = filtered.filter(o => format(new Date(o.createdAt), 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd'));
        return { label: timeRange === '7days' ? format(day, 'EEE') : format(day, 'M/d'), revenue: dayOrders.reduce((s, o) => s + (o.totals?.total || 0), 0), count: dayOrders.length };
      });
      // For 30/90 days, sample every nth day for readability
      const step = timeRange === '90days' ? 7 : timeRange === '30days' ? 2 : 1;
      const sampled = data.filter((_, i) => i % step === 0 || i === data.length - 1);
      setChartData({ revenue: sampled.map(d => ({ label: d.label, value: d.revenue })), orders: sampled.map(d => ({ label: d.label, value: d.count })) });
    }
  };

  // Computed metrics
  const getFilteredOrders = () => {
    let f = orders;
    if (selectedCustomer !== 'all') f = f.filter(o => o.businessId?._id === selectedCustomer);
    return f;
  };
  const getCompletedOrders = () => getFilteredOrders().filter(o => ['delivered', 'shipped'].includes(o.status));

  const filteredAll = getFilteredOrders();
  const completedOrders = getCompletedOrders();
  const totalRevenue = completedOrders.reduce((s, o) => s + (o.totals?.total || 0), 0);
  const avgOrder = completedOrders.length > 0 ? totalRevenue / completedOrders.length : 0;

  // Status breakdown
  const statusBreakdown = {};
  filteredAll.forEach(o => { statusBreakdown[o.status] = (statusBreakdown[o.status] || 0) + 1; });

  // Top customers
  const getTopCustomers = (limit = 7) => {
    const map = {};
    completedOrders.forEach(o => {
      const id = o.businessId?._id;
      if (!id) return;
      if (!map[id]) map[id] = { name: o.businessId.businessName || 'Unknown', orders: 0, revenue: 0, type: o.businessId.businessType || '' };
      map[id].orders++; map[id].revenue += o.totals?.total || 0;
    });
    return Object.values(map).sort((a, b) => b.revenue - a.revenue).slice(0, limit);
  };

  // Top products
  const getTopProducts = (limit = 7) => {
    const map = {};
    completedOrders.forEach(o => {
      (o.items || []).forEach(item => {
        const id = item.productId?._id || item.productId;
        if (!id) return;
        if (!map[id]) map[id] = { name: item.name || item.productName || 'Unknown', qty: 0, revenue: 0, orders: 0 };
        map[id].qty += item.quantity || 0; map[id].revenue += item.totalPrice || ((item.unitPrice || 0) * (item.quantity || 0)); map[id].orders++;
      });
    });
    return Object.values(map).sort((a, b) => b.revenue - a.revenue).slice(0, limit);
  };

  // Per-customer purchase detail (Pro+/Enterprise)
  const getCustomerPurchaseDetail = () => {
    const custMap = {};
    completedOrders.forEach(o => {
      const cid = o.businessId?._id;
      if (!cid) return;
      if (!custMap[cid]) custMap[cid] = { name: o.businessId.businessName || 'Unknown', type: o.businessId.businessType || '', products: {}, totalRevenue: 0, orderCount: 0 };
      custMap[cid].totalRevenue += o.totals?.total || 0;
      custMap[cid].orderCount++;
      (o.items || []).forEach(item => {
        const pid = item.productId?._id || item.productId;
        if (!pid) return;
        if (!custMap[cid].products[pid]) custMap[cid].products[pid] = { name: item.name || 'Unknown', qty: 0, revenue: 0 };
        custMap[cid].products[pid].qty += item.quantity || 0;
        custMap[cid].products[pid].revenue += item.totalPrice || ((item.unitPrice || 0) * (item.quantity || 0));
      });
    });
    return Object.values(custMap)
      .map(c => ({ ...c, products: Object.values(c.products).sort((a, b) => b.revenue - a.revenue) }))
      .sort((a, b) => b.totalRevenue - a.totalRevenue);
  };

  const defaultLimit = tier === 'enterprise' ? 15 : tier === 'professional' ? 10 : 7;
  const topCustomers = getTopCustomers(expandedCustomers ? 50 : defaultLimit);
  const topProducts = getTopProducts(expandedProducts ? 50 : defaultLimit);
  const customerDetail = (tier !== 'starter') ? getCustomerPurchaseDetail() : [];
  const productStats = {
    total: products.length,
    active: products.filter(p => p.status === 'active').length,
    inStock: products.filter(p => p.stockStatus === 'in_stock' || p.availability?.inStock).length,
    lowStock: products.filter(p => p.stockStatus === 'low_stock').length,
    outOfStock: products.filter(p => p.stockStatus === 'out_of_stock').length,
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50"><Navbar />
      <div className="flex justify-center items-center py-32"><LoadingSpinner size="lg" /></div>
    <Footer /></div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-16 lg:pt-0">
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-blue-500" /> {t('analytics.title', 'Analytics')}
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                tier === 'enterprise' ? 'bg-amber-100 text-amber-700' :
                tier === 'professional' ? 'bg-purple-100 text-purple-700' :
                'bg-blue-100 text-blue-700'
              }`}>
                {t(`subscription.tiers.${tier}`, tier?.charAt(0).toUpperCase() + tier?.slice(1))}
              </span>
            </h1>
            <p className="text-sm text-gray-500 mt-1">{t('analytics.subtitle', 'Your business performance and insights')}</p>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl border p-4 mb-6">
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-2">{t('analytics.timeRange', 'Time Range')}</label>
                <div className="flex gap-1.5">
                  {[{ v: '7days', l: t('analytics.7days', '7 Days') }, { v: '30days', l: t('analytics.30days', '30 Days') }, { v: '90days', l: t('analytics.90days', '90 Days') }, { v: 'year', l: t('analytics.year', 'Year') }].map(r => (
                    <button key={r.v} onClick={() => setTimeRange(r.v)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        timeRange === r.v ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}>{r.l}</button>
                  ))}
                </div>
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs font-medium text-gray-500 block mb-2">{t('analytics.filterByCustomer', 'Filter by Customer')}</label>
                <select value={selectedCustomer} onChange={e => setSelectedCustomer(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                  <option value="all">{t('analytics.allCustomers', 'All Customers')} ({businesses.length})</option>
                  {businesses.map(b => <option key={b._id} value={b._id}>{b.businessName}</option>)}
                </select>
              </div>
              {selectedCustomer !== 'all' && (
                <button onClick={() => setSelectedCustomer('all')}
                  className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg border">{t('analytics.clearFilter', 'Clear Filter')}</button>
              )}
            </div>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl border p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-500">{ t('analytics.totalOrders', 'Total Orders') }</span>
                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center"><ShoppingCart className="w-4 h-4 text-blue-500" /></div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{filteredAll.length}</p>
              <p className="text-xs text-gray-400 mt-1">{completedOrders.length} {t('analytics.completed', 'completed')}</p>
            </div>
            <div className="bg-white rounded-xl border p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-500">{ t('analytics.revenue', 'Revenue') }</span>
                <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center"><Euro className="w-4 h-4 text-green-500" /></div>
              </div>
              <p className="text-2xl font-bold text-green-600">€{totalRevenue.toFixed(2)}</p>
              <p className="text-xs text-gray-400 mt-1">{ t('analytics.fromCompletedOrders', 'From completed orders') }</p>
            </div>
            <div className="bg-white rounded-xl border p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-500">{ t('analytics.avgOrder', 'Avg Order') }</span>
                <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center"><TrendingUp className="w-4 h-4 text-purple-500" /></div>
              </div>
              <p className="text-2xl font-bold text-purple-600">€{avgOrder.toFixed(2)}</p>
              <p className="text-xs text-gray-400 mt-1">{ t('analytics.perOrder', 'Per order') }</p>
            </div>
            <div className="bg-white rounded-xl border p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-500">{ t('analytics.customers', 'Customers') }</span>
                <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center"><Users className="w-4 h-4 text-amber-500" /></div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{businesses.length}</p>
              <p className="text-xs text-gray-400 mt-1">{ t('analytics.uniqueBusinesses', 'Unique businesses') }</p>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <div className="bg-white rounded-xl border p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">{ t('analytics.revenueOverTime', 'Revenue Over Time') }</h3>
              <BarChart data={chartData.revenue} color="#10b981" valuePrefix="€" />
            </div>
            <div className="bg-white rounded-xl border p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">{ t('analytics.ordersOverTime', 'Orders Over Time') }</h3>
              <BarChart data={chartData.orders} color="#6366f1" />
            </div>
          </div>

          {/* Order Status + Product Stats */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-xl border p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">{ t('analytics.orderStatus', 'Order Status') }</h3>
              <DonutChart data={statusBreakdown} />
            </div>
            <div className="bg-white rounded-xl border p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">{ t('analytics.productInventory', 'Product Inventory') }</h3>
              <div className="space-y-3 mt-2">
                {[
                  { label: t('analytics.totalProducts', 'Total Products'), val: productStats.total, bg: 'bg-gray-50', text: 'text-gray-900' },
                  { label: t('analytics.active', 'Active'), val: productStats.active, bg: 'bg-green-50', text: 'text-green-700' },
                  { label: t('analytics.inStock', 'In Stock'), val: productStats.inStock, bg: 'bg-blue-50', text: 'text-blue-700' },
                  { label: t('analytics.lowStock', 'Low Stock'), val: productStats.lowStock, bg: 'bg-yellow-50', text: 'text-yellow-700' },
                  { label: t('analytics.outOfStock', 'Out of Stock'), val: productStats.outOfStock, bg: 'bg-red-50', text: 'text-red-700' },
                ].map(s => (
                  <div key={s.label} className={`flex justify-between items-center px-3 py-2.5 rounded-lg ${s.bg}`}>
                    <span className={`text-sm font-medium ${s.text}`}>{s.label}</span>
                    <span className={`text-sm font-bold ${s.text}`}>{s.val}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Customer Overview in selected mode */}
            {selectedCustomer !== 'all' ? (
              <div className="bg-white rounded-xl border p-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">{ t('analytics.customerSummary', 'Customer Summary') }</h3>
                {(() => {
                  const b = businesses.find(b => b._id === selectedCustomer);
                  return b ? (
                    <div className="space-y-3">
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm font-semibold text-blue-900">{b.businessName}</p>
                        <p className="text-xs text-blue-600 capitalize">{b.businessType?.replace('_', ' ') || ''}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="text-center p-2 bg-gray-50 rounded-lg">
                          <p className="text-lg font-bold text-gray-900">{filteredAll.length}</p>
                          <p className="text-xs text-gray-500">{ t('analytics.ordersLabel', 'Orders') }</p>
                        </div>
                        <div className="text-center p-2 bg-gray-50 rounded-lg">
                          <p className="text-lg font-bold text-green-600">€{totalRevenue.toFixed(0)}</p>
                          <p className="text-xs text-gray-500">{ t('analytics.revenue', 'Revenue') }</p>
                        </div>
                      </div>
                      <div className="text-center p-2 bg-gray-50 rounded-lg">
                        <p className="text-lg font-bold text-purple-600">€{avgOrder.toFixed(2)}</p>
                        <p className="text-xs text-gray-500">{ t('analytics.averageOrderValue', 'Avg Order Value') }</p>
                      </div>
                    </div>
                  ) : null;
                })()}
              </div>
            ) : (
              <div className="bg-white rounded-xl border p-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">{ t('analytics.atAGlance', 'At a Glance') }</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center px-3 py-2.5 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">{ t('analytics.cancellationRate', 'Cancellation Rate') }</span>
                    <span className="text-sm font-bold text-gray-900">
                      {filteredAll.length > 0 ? ((statusBreakdown.cancelled || 0) / filteredAll.length * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center px-3 py-2.5 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">{ t('analytics.completionRate', 'Completion Rate') }</span>
                    <span className="text-sm font-bold text-green-600">
                      {filteredAll.length > 0 ? (completedOrders.length / filteredAll.length * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center px-3 py-2.5 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">{ t('analytics.pendingOrders', 'Pending Orders') }</span>
                    <span className="text-sm font-bold text-yellow-600">{statusBreakdown.pending || 0}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Top Customers & Top Products — Advanced analytics (Professional+) */}
          {tier === 'starter' ? (
            <div className="bg-white rounded-xl border p-8 text-center">
              <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Lock className="w-7 h-7 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('analytics.advancedInsights', 'Advanced Insights')}
              </h3>
              <p className="text-sm text-gray-500 mb-1">
                {t('analytics.advancedInsightsDesc', 'Top customers, top products, and detailed breakdowns are available on Professional and Enterprise plans.')}
              </p>
              <p className="text-xs text-gray-400 mb-4">
                {t('analytics.currentTier', 'Current plan: {{tier}}', { tier: t(`subscription.tiers.${tier}`, 'Starter') })}
              </p>
              <button
                onClick={() => navigate('/wholesaler/pricing')}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium text-sm"
              >
                <TrendingUp className="w-4 h-4" />
                {t('analytics.upgradeForInsights', 'Upgrade for Advanced Insights')}
              </button>
            </div>
          ) : (
          <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900">{ t('analytics.topCustomers', 'Top Customers') }</h3>
                {getTopCustomers(50).length > defaultLimit && (
                  <button onClick={() => setExpandedCustomers(!expandedCustomers)}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                    {expandedCustomers ? t('analytics.showLess', 'Show Less') : t('analytics.showAll', `Show All (${getTopCustomers(50).length})`)}
                  </button>
                )}
              </div>
              {topCustomers.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">{ t('analytics.noDataYet', 'No data yet') }</p>
              ) : (
                <div className={`space-y-3 ${expandedCustomers ? 'max-h-96 overflow-y-auto pr-2' : ''}`}>
                  {topCustomers.map((c, i) => {
                    const max = topCustomers[0]?.revenue || 1;
                    return (
                      <div key={i}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 ${
                              i === 0 ? 'bg-yellow-400' : i === 1 ? 'bg-gray-400' : i === 2 ? 'bg-amber-600' : 'bg-gray-200 text-gray-500'
                            }`}>{i + 1}</span>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{c.name}</p>
                              <p className="text-xs text-gray-400">{c.orders} {t('analytics.ordersLabel', 'orders')} • {c.type?.replace('_', ' ') || ''}</p>
                            </div>
                          </div>
                          <span className="text-sm font-bold text-green-600 flex-shrink-0 ml-2">€{c.revenue.toFixed(0)}</span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden ml-8">
                          <div className="h-full bg-gradient-to-r from-emerald-400 to-green-500 rounded-full" style={{ width: `${(c.revenue / max) * 100}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="bg-white rounded-xl border p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900">{ t('analytics.topProducts', 'Top Products') }</h3>
                {getTopProducts(50).length > defaultLimit && (
                  <button onClick={() => setExpandedProducts(!expandedProducts)}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                    {expandedProducts ? t('analytics.showLess', 'Show Less') : t('analytics.showAll', `Show All (${getTopProducts(50).length})`)}
                  </button>
                )}
              </div>
              {topProducts.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">{ t('analytics.noDataYet', 'No data yet') }</p>
              ) : (
                <div className={`space-y-3 ${expandedProducts ? 'max-h-96 overflow-y-auto pr-2' : ''}`}>
                  {topProducts.map((p, i) => {
                    const max = topProducts[0]?.revenue || 1;
                    return (
                      <div key={i}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 ${
                              i === 0 ? 'bg-yellow-400' : i === 1 ? 'bg-gray-400' : i === 2 ? 'bg-amber-600' : 'bg-gray-200 text-gray-500'
                            }`}>{i + 1}</span>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
                              <p className="text-xs text-gray-400">{p.qty} {t('analytics.units', 'units')} • {p.orders} {t('analytics.ordersLabel', 'orders')}</p>
                            </div>
                          </div>
                          <span className="text-sm font-bold text-blue-600 flex-shrink-0 ml-2">€{p.revenue.toFixed(0)}</span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden ml-8">
                          <div className="h-full bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full" style={{ width: `${(p.revenue / max) * 100}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Per-Customer Purchase Detail (Pro+/Enterprise) */}
          {tier !== 'starter' && customerDetail.length > 0 && (
            <div className="bg-white rounded-xl border p-6 mt-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">{t('analytics.customerPurchaseDetail', 'Customer Purchase Detail')}</h3>
              <div className="space-y-2">
                {customerDetail.map((cust, ci) => (
                  <div key={ci} className="border rounded-lg overflow-hidden">
                    <button
                      onClick={() => setExpandedCustomerDetail(expandedCustomerDetail === ci ? null : ci)}
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 ${
                          ci === 0 ? 'bg-yellow-400' : ci === 1 ? 'bg-gray-400' : ci === 2 ? 'bg-amber-600' : 'bg-gray-200 text-gray-500'
                        }`}>{ci + 1}</span>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{cust.name}</p>
                          <p className="text-xs text-gray-400">{cust.orderCount} {t('analytics.ordersLabel', 'orders')} • {cust.products.length} {t('analytics.productsOrdered', 'products')} • {cust.type?.replace('_', ' ') || ''}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className="text-sm font-bold text-green-600">€{cust.totalRevenue.toFixed(0)}</span>
                        <svg className={`w-4 h-4 text-gray-400 transition-transform ${expandedCustomerDetail === ci ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </button>
                    {expandedCustomerDetail === ci && (
                      <div className="px-4 pb-3 border-t bg-gray-50">
                        <div className="grid grid-cols-12 gap-2 py-2 text-xs font-medium text-gray-500 border-b">
                          <span className="col-span-6">{t('analytics.productName', 'Product')}</span>
                          <span className="col-span-2 text-right">{t('analytics.qtyLabel', 'Qty')}</span>
                          <span className="col-span-4 text-right">{t('analytics.revenueLabel', 'Revenue')}</span>
                        </div>
                        {cust.products.map((prod, pi) => (
                          <div key={pi} className="grid grid-cols-12 gap-2 py-2 text-sm border-b border-gray-100 last:border-0">
                            <span className="col-span-6 text-gray-700 truncate">{prod.name}</span>
                            <span className="col-span-2 text-right text-gray-500">{prod.qty}</span>
                            <span className="col-span-4 text-right font-medium text-gray-900">€{prod.revenue.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          </>
          )}

          {/* ================================================================ */}
          {/* ENTERPRISE-EXCLUSIVE: Premium Analytics Suite                     */}
          {/* ================================================================ */}
          {tier === 'enterprise' && completedOrders.length > 0 && (() => {
            // ── Repeat customer analysis ──
            const customerOrderCounts = {};
            filteredAll.forEach(o => {
              const id = o.businessId?._id;
              if (id) customerOrderCounts[id] = (customerOrderCounts[id] || 0) + 1;
            });
            const repeatCustomers = Object.values(customerOrderCounts).filter(c => c > 1).length;
            const totalCustomersCount = Object.keys(customerOrderCounts).length;
            const retentionRate = totalCustomersCount > 0 ? (repeatCustomers / totalCustomersCount * 100) : 0;

            // ── Average order fulfillment time (createdAt → updatedAt for delivered) ──
            // Order model has no statusHistory, so we use timestamps
            const deliveredOrders = filteredAll.filter(o => o.status === 'delivered' && o.createdAt && o.updatedAt);
            let avgFulfillmentHours = 0;
            if (deliveredOrders.length > 0) {
              const totalHours = deliveredOrders.reduce((sum, o) => {
                const created = new Date(o.createdAt);
                const updated = new Date(o.updatedAt);
                const diff = (updated - created) / (1000 * 60 * 60);
                return sum + (diff > 0 ? diff : 0);
              }, 0);
              avgFulfillmentHours = totalHours / deliveredOrders.length;
            }

            // ── Revenue by category — look up from products array since orders may not store category ──
            const productCategoryMap = {};
            products.forEach(p => {
              const pid = p._id || p.id;
              if (pid) productCategoryMap[pid] = p.category || '';
            });

            const categoryRevenue = {};
            completedOrders.forEach(o => {
              (o.items || []).forEach(item => {
                const pid = item.productId?._id || item.productId;
                // Try item.category first, then look up from products
                const cat = item.category || (pid && productCategoryMap[pid]) || (pid && productCategoryMap[pid?.toString?.()]) || '';
                const displayCat = cat || t('analytics.uncategorized', 'Uncategorized');
                if (!categoryRevenue[displayCat]) categoryRevenue[displayCat] = { revenue: 0, qty: 0 };
                categoryRevenue[displayCat].revenue += item.totalPrice || ((item.unitPrice || 0) * (item.quantity || 0));
                categoryRevenue[displayCat].qty += item.quantity || 0;
              });
            });
            const topCategories = Object.entries(categoryRevenue)
              .map(([name, d]) => ({ name, ...d }))
              .sort((a, b) => b.revenue - a.revenue)
              .slice(0, 6);
            const catMax = topCategories[0]?.revenue || 1;

            // ── AOV trend ──
            const aovData = chartData.revenue.map((d, i) => {
              const ordersVal = chartData.orders[i]?.value || 0;
              return { label: d.label, value: ordersVal > 0 ? Math.round(d.value / ordersVal) : 0 };
            });

            // ── Peak ordering hours ──
            const hourCounts = new Array(24).fill(0);
            filteredAll.forEach(o => {
              const h = new Date(o.createdAt).getHours();
              hourCounts[h]++;
            });
            const peakHour = hourCounts.indexOf(Math.max(...hourCounts));
            const peakHourLabel = `${peakHour.toString().padStart(2, '0')}:00 - ${((peakHour + 1) % 24).toString().padStart(2, '0')}:00`;

            return (
              <>
                {/* Enterprise Badge */}
                <div className="mt-8 mb-4 flex items-center gap-2">
                  <div className="h-px flex-1 bg-gradient-to-r from-amber-200 to-transparent" />
                  <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1 rounded-full">
                    ⭐ {t('analytics.premiumInsights', 'Premium Analytics Suite')}
                  </span>
                  <div className="h-px flex-1 bg-gradient-to-l from-amber-200 to-transparent" />
                </div>

                {/* Row 1: Retention + Fulfillment + Peak Hours */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-white rounded-xl border p-5">
                    <h4 className="text-xs font-medium text-gray-500 mb-3">{t('analytics.customerRetention', 'Customer Retention')}</h4>
                    <p className="text-3xl font-bold text-indigo-600">{retentionRate.toFixed(1)}%</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {repeatCustomers} {t('analytics.repeatOf', 'repeat of')} {totalCustomersCount} {t('analytics.customers', 'customers')}
                    </p>
                    <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${Math.min(retentionRate, 100)}%` }} />
                    </div>
                  </div>

                  <div className="bg-white rounded-xl border p-5">
                    <h4 className="text-xs font-medium text-gray-500 mb-3">{t('analytics.avgFulfillment', 'Avg. Fulfillment Time')}</h4>
                    <p className="text-3xl font-bold text-teal-600">
                      {avgFulfillmentHours < 24
                        ? `${avgFulfillmentHours.toFixed(1)}h`
                        : `${(avgFulfillmentHours / 24).toFixed(1)}d`
                      }
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {t('analytics.confirmedToDelivered', 'Confirmed → Delivered')}
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      {deliveredOrders.length} {t('analytics.ordersAnalyzed', 'orders analyzed')}
                    </p>
                  </div>

                  <div className="bg-white rounded-xl border p-5">
                    <h4 className="text-xs font-medium text-gray-500 mb-3">{t('analytics.peakOrdering', 'Peak Ordering Time')}</h4>
                    <p className="text-3xl font-bold text-orange-600">{peakHourLabel}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {Math.max(...hourCounts)} {t('analytics.ordersInPeak', 'orders in this window')}
                    </p>
                    <div className="flex gap-px mt-3 h-8 items-end">
                      {hourCounts.filter((_, i) => i >= 6 && i <= 22).map((count, i) => {
                        const maxH = Math.max(...hourCounts.filter((_, j) => j >= 6 && j <= 22), 1);
                        return (
                          <div key={i} className={`flex-1 rounded-t ${i + 6 === peakHour ? 'bg-orange-500' : 'bg-gray-200'}`}
                            style={{ height: `${Math.max((count / maxH) * 100, 4)}%` }}
                            title={`${(i + 6).toString().padStart(2, '0')}:00 — ${count} orders`} />
                        );
                      })}
                    </div>
                    <div className="flex justify-between text-[9px] text-gray-400 mt-1">
                      <span>06:00</span><span>14:00</span><span>22:00</span>
                    </div>
                  </div>
                </div>

                {/* Row 2: AOV Trend + Revenue by Category */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl border p-6">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">{t('analytics.aovTrend', 'Average Order Value Trend')}</h4>
                    <BarChart data={aovData} color="#8b5cf6" valuePrefix="€" height={180} />
                  </div>

                  <div className="bg-white rounded-xl border p-6">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">{t('analytics.revenueByCategory', 'Revenue by Category')}</h4>
                    {topCategories.length === 0 ? (
                      <p className="text-sm text-gray-400 text-center py-6">No category data</p>
                    ) : (
                      <div className="space-y-3">
                        {topCategories.map((cat, i) => (
                          <div key={i}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm text-gray-700 capitalize truncate">{cat.name.replace(/_/g, ' ')}</span>
                              <span className="text-sm font-bold text-emerald-600 ml-2">€{cat.revenue.toFixed(0)}</span>
                            </div>
                            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-full bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full" style={{ width: `${(cat.revenue / catMax) * 100}%` }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            );
          })()}
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default WholesalerAnalytics;